import React, { useState, useEffect, useLayoutEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';
import { Split } from 'lucide-react';
import { BrokerOrder, BrokerPosition, useActiveBrokerBalance } from '../store/brokerStore';
import { roundToTick, calculatePnL } from '../config/instruments';
import { useToastStore } from '../store/toastStore';
import { useMarketStore } from '../store/marketStore';
import SplitOrderModal from './SplitOrderModal';
import { labelYInPane } from './Chart/PricePaneClip';
import './LiveOrderLine.css';

/**
 * Seconds left before a resting entry is cancelled, or null when it has no
 * expiry or the deadline has passed.
 *
 * Ticks locally off an ABSOLUTE deadline supplied by tradovate-service.
 * Tradovate has no GTD, so the expiry is a timer on that side and the order
 * itself says nothing about it — a resting entry with ten seconds left looks
 * identical to one that will sit all session.
 *
 * Local ticking rather than a pushed countdown: the deadline is fixed, so one
 * broadcast is enough and the display cannot drift with the poll interval.
 */
function useExpiryCountdown(expiresAt: number | undefined): number | null {
  const [remaining, setRemaining] = useState<number | null>(() =>
    expiresAt ? Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000)) : null
  );

  useEffect(() => {
    if (!expiresAt) {
      setRemaining(null);
      return;
    }
    const tick = () => {
      const left = Math.ceil((expiresAt - Date.now()) / 1000);
      setRemaining(left > 0 ? left : null);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [expiresAt]);

  return remaining;
}

/** m:ss under a minute rolls to plain seconds — "0:07" reads slower than "7s". */
function formatCountdown(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export interface LiveOrderLineProps {
  order: BrokerOrder & {
    price?: number;
    stopPrice?: number;
    ordType?: string;
    orderQty?: number;
    /** Epoch ms the bot will cancel this resting entry at, if it has an expiry. */
    expiresAt?: number;
  };
  yPosition: number;
  labelOffset?: number; // Vertical offset for the pill/label to avoid overlapping with position line
  horizontalOffset?: number;
  position?: BrokerPosition | null; // Current position for P&L calculation
  onCoordinateToPrice: (yCoordinate: number) => number | null;
  onModifyOrder: (orderId: number, newPrice: number, isStopPrice: boolean, brokerId?: string) => void;
  onCancelOrder: (orderId: number, brokerId?: string) => void;
  onSplitOrder?: (order: BrokerOrder, qty1: number, qty2: number, price1: number, price2: number) => void;
  /**
   * Bottom of the price pane (px) while order-flow line panes sit under it
   * (PricePaneClip), null without them. The control label is kept wholly
   * inside the pane and hidden with its line when the line is below it.
   */
  paneBottom?: number | null;
}

/** Half the control label's height before it has been measured, px. */
const LABEL_HALF_PX = 11;

export interface LiveOrderLineHandle {
  updateY: (y: number, labelOffset: number) => void;
}

const LiveOrderLine = forwardRef<LiveOrderLineHandle, LiveOrderLineProps>(({
  order,
  yPosition,
  labelOffset = 0,
  horizontalOffset = 0,
  position,
  onCoordinateToPrice,
  onModifyOrder,
  onCancelOrder,
  onSplitOrder,
  paneBottom = null,
}, ref) => {
  const chartSymbol = useMarketStore(state => state.symbol);
  const { realizedPnL } = useActiveBrokerBalance();
  const [isDragging, setIsDragging] = useState(false);
  const orderPrice = order.stopPrice || order.price || 0;
  const expirySeconds = useExpiryCountdown(order.expiresAt);

  const orderLineRef = useRef<HTMLDivElement>(null);
  const connectorRef = useRef<HTMLDivElement>(null);
  const controlsElRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);

  useEffect(() => { isDraggingRef.current = isDragging; }, [isDragging]);

  // The label's measured half height (layout effect), and the latest pane
  // bottom for the imperative updateY (synced after commit, not in render).
  const [labelHalf, setLabelHalf] = useState(LABEL_HALF_PX);
  const labelHalfRef = useRef(LABEL_HALF_PX);
  const paneBottomRef = useRef(paneBottom);
  useLayoutEffect(() => {
    paneBottomRef.current = paneBottom;
    const h = (controlsElRef.current?.offsetHeight ?? 0) / 2;
    if (h > 0 && h !== labelHalfRef.current) {
      labelHalfRef.current = h;
      setLabelHalf(h);
    }
  });

  useImperativeHandle(ref, () => ({
    updateY: (y: number, labelOffset: number) => {
      if (isDraggingRef.current) return;
      if (orderLineRef.current) orderLineRef.current.style.top = `${y}px`;
      const labelTop = labelYInPane(y, y + labelOffset, labelHalfRef.current, paneBottomRef.current);
      const shown = labelTop ?? y;
      if (connectorRef.current) {
        connectorRef.current.style.top = `${Math.min(y, shown)}px`;
        connectorRef.current.style.height = `${Math.abs(shown - y)}px`;
      }
      if (controlsElRef.current) {
        controlsElRef.current.style.top = `${shown}px`;
        controlsElRef.current.style.visibility = labelTop === null ? 'hidden' : '';
      }
    },
  }));

  const [dragPrice, setDragPrice] = useState<number>(orderPrice);
  const [dragY, setDragY] = useState<number>(yPosition);
  // Track pending price to show optimistic update until server confirms
  const [pendingPrice, setPendingPrice] = useState<number | null>(null);
  const [pendingY, setPendingY] = useState<number | null>(null);

  const isBuy = order.action === 'Buy';
  const isStopOrder = order.ordType?.toLowerCase().includes('stop') ?? false;
  const orderQty = order.orderQty || 1;

  // Split order modal state
  const [showSplitModal, setShowSplitModal] = useState(false);
  const canSplit = orderQty >= 2 && onSplitOrder !== undefined;
  const isLongPosition = position ? position.netPos > 0 : !isBuy; // Infer from order action if no position

  // Clear pending state when server confirms the new price
  useEffect(() => {
    if (pendingPrice !== null) {
      // Check if order price is close enough to pending price (within 1 tick)
      const priceDiff = Math.abs(orderPrice - pendingPrice);
      console.log(`[LiveOrderLine] Order ${order.id}: orderPrice=${orderPrice}, pendingPrice=${pendingPrice}, diff=${priceDiff.toFixed(2)}`);
      if (priceDiff < 0.5) {
        // Server confirmed the new price
        console.log(`[LiveOrderLine] Order ${order.id}: Server confirmed new price, clearing pending state`);
        setPendingPrice(null);
        setPendingY(null);
      }
    }
  }, [orderPrice, pendingPrice, order.id]);

  // Clear pending state after timeout (in case server doesn't confirm)
  useEffect(() => {
    if (pendingPrice !== null) {
      const timeout = setTimeout(() => {
        setPendingPrice(null);
        setPendingY(null);
      }, 5000); // 5 second timeout
      return () => clearTimeout(timeout);
    }
  }, [pendingPrice]);

  // Calculate P&L if we have a position
  // For resting orders (SL/TP), calculate what P&L would be if order fills
  let pnl: number | null = null;
  if (position && position.netPos !== 0 && position.netPrice > 0) {
    // Use pending or drag price for P&L calculation to show optimistic update
    const pnlDisplayPrice = isDragging ? dragPrice : (pendingPrice !== null ? pendingPrice : orderPrice);
    // Use order quantity for the P&L calculation, with position direction
    const positionDirection = position.netPos > 0 ? 1 : -1;
    // Use order's symbol for correct tick value calculation
    const symbol = order.symbol || position.symbol || chartSymbol;
    const pnlResult = calculatePnL(position.netPrice, pnlDisplayPrice, positionDirection * orderQty, symbol);
    pnl = pnlResult.dollars;
  }

  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    // Don't start drag if clicking on a button
    if ((e.target as HTMLElement).tagName === 'BUTTON') {
      return;
    }

    e.stopPropagation();
    // Don't preventDefault on touch events to allow scrolling to work normally when not dragging
    if ('preventDefault' in e && e.type !== 'touchstart') {
      e.preventDefault();
    }
    setIsDragging(true);
    setDragPrice(orderPrice);
    setDragY(yPosition);
  };

  const addToast = useToastStore(state => state.addToast);

  const handleCancelOrder = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const orderType = isStopOrder ? 'SL' : 'TP';
    addToast({ type: 'info', message: `Canceling ${orderType} order @ ${orderPrice.toFixed(2)}...` });
    onCancelOrder(order.id, order.brokerId);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;

      // Get chart container to calculate relative Y position
      const chartContainer = document.querySelector('.chart-container');
      if (!chartContainer) {
        console.log('[LiveOrderLine] handleMouseMove: chartContainer not found');
        return;
      }

      const rect = chartContainer.getBoundingClientRect();
      const relativeY = e.clientY - rect.top;

      // Update visual Y position
      setDragY(relativeY);

      // Use the callback to convert Y coordinate to price
      const newPrice = onCoordinateToPrice(relativeY);
      // Use order.symbol if available, otherwise fall back to chart symbol
      const symbolForSnap = order.symbol || chartSymbol;

      if (newPrice !== null && symbolForSnap) {
        // Snap to tick size using order symbol or chart symbol
        const snappedPrice = roundToTick(newPrice, symbolForSnap);
        setDragPrice(snappedPrice);
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isDragging) return;

      // Prevent scrolling while dragging
      e.preventDefault();

      // Get chart container to calculate relative Y position
      const chartContainer = document.querySelector('.chart-container');
      if (!chartContainer) return;

      const rect = chartContainer.getBoundingClientRect();
      const touch = e.touches[0];
      const relativeY = touch.clientY - rect.top;

      // Update visual Y position
      setDragY(relativeY);

      // Use the callback to convert Y coordinate to price
      const newPrice = onCoordinateToPrice(relativeY);
      // Use order.symbol if available, otherwise fall back to chart symbol
      const symbolForSnap = order.symbol || chartSymbol;

      if (newPrice !== null && symbolForSnap) {
        // Snap to tick size using order symbol or chart symbol
        const snappedPrice = roundToTick(newPrice, symbolForSnap);
        setDragPrice(snappedPrice);
      }
    };

    const handleMouseUp = () => {
      if (isDragging && dragPrice !== orderPrice) {
        // Set pending state for optimistic UI update
        setPendingPrice(dragPrice);
        setPendingY(dragY);
        // Modify the order with new price
        const orderType = isStopOrder ? 'SL' : 'TP';
        addToast({ type: 'info', message: `Moving ${orderType}: ${orderPrice.toFixed(2)} → ${dragPrice.toFixed(2)}` });
        onModifyOrder(order.id, dragPrice, isStopOrder, order.brokerId);
      }
      setIsDragging(false);
    };

    const handleTouchEnd = () => {
      if (isDragging && dragPrice !== orderPrice) {
        // Set pending state for optimistic UI update
        setPendingPrice(dragPrice);
        setPendingY(dragY);
        // Modify the order with new price
        const orderType = isStopOrder ? 'SL' : 'TP';
        addToast({ type: 'info', message: `Moving ${orderType}: ${orderPrice.toFixed(2)} → ${dragPrice.toFixed(2)}` });
        onModifyOrder(order.id, dragPrice, isStopOrder, order.brokerId);
      }
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, dragPrice, order.id, orderPrice, isStopOrder, onCoordinateToPrice, onModifyOrder]);

  // Use dragY when dragging, pendingY for optimistic update, otherwise use the prop yPosition
  const displayY = isDragging ? dragY : (pendingY !== null ? pendingY : yPosition);
  // Also display pending price while waiting for confirmation
  const displayPrice = isDragging ? dragPrice : (pendingPrice !== null ? pendingPrice : orderPrice);

  const isPending = pendingPrice !== null;
  const labelTop = labelYInPane(displayY, displayY + (isDragging ? 0 : labelOffset), labelHalf, paneBottom);
  const shownLabelTop = labelTop ?? displayY;
  const connectorOffset = shownLabelTop - displayY;

  return (
    <>
      {/* Horizontal line extending to the left */}
      <div
        ref={orderLineRef}
        className={`live-order-line ${isBuy ? 'buy' : 'sell'} ${isStopOrder ? 'stop' : 'limit'} ${isDragging ? 'dragging' : ''} ${isPending ? 'pending' : ''}`}
        style={{
          top: `${displayY}px`,
          right: `${80 + horizontalOffset}px`,
        }}
      />
      {/* Arrow connector from displaced label to line */}
      {labelOffset !== 0 && !isDragging && (
        <div
          ref={connectorRef}
          className={`live-order-label-connector ${isBuy ? 'buy' : 'sell'}`}
          style={{
            top: `${Math.min(displayY, shownLabelTop)}px`,
            right: `${88 + horizontalOffset}px`,
            height: `${Math.abs(connectorOffset)}px`,
          }}
        >
          <div className={`connector-arrow ${labelOffset > 0 ? 'arrow-up' : 'arrow-down'}`} />
        </div>
      )}
      {/* Control label - offset vertically when overlapping with position line */}
      <div
        ref={controlsElRef}
        className={`live-order-line-controls ${isDragging ? 'dragging' : ''} ${isBuy ? 'buy' : 'sell'} ${isStopOrder ? 'stop' : 'limit'} ${isPending ? 'pending' : ''}`}
        style={{
          top: `${shownLabelTop}px`,
          right: `${80 + horizontalOffset}px`,
          transform: 'translateY(-50%)',
          cursor: isDragging ? 'grabbing' : 'grab',
          visibility: labelTop === null ? 'hidden' : undefined,
        }}
        data-testid="live-order-line-controls"
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
      >
        <span className="live-order-badge order">ORD</span>
        <span className="live-order-subtype">{isStopOrder ? 'STP' : 'LMT'}</span>
        <span className="live-order-price">{displayPrice.toFixed(2)}</span>
        <span className="live-order-quantity">x{order.orderQty || 1}</span>
        {/* Time left before the bot cancels this resting entry. Only shown when
            there IS an expiry — most orders have none, and a blank slot on
            those is quieter than a dash. Turns urgent under 30s, which on a
            6-minute expiry is the point where it stops being background. */}
        {expirySeconds !== null && (
          <span
            className={`live-order-expiry ${expirySeconds <= 30 ? 'urgent' : ''}`}
            title="Cancels if unfilled"
          >
            {formatCountdown(expirySeconds)}
          </span>
        )}
        {pnl !== null && (
          <span className={`live-order-pnl ${pnl >= 0 ? 'profit' : 'loss'}`}>
            {pnl >= 0 ? '+' : ''}{pnl.toFixed(2)}
          </span>
        )}
        {pnl !== null && (
          <span className={`live-order-pnl day-pnl ${(realizedPnL + pnl) >= 0 ? 'profit' : 'loss'}`}>
            D:{(realizedPnL + pnl) >= 0 ? '+' : ''}{(realizedPnL + pnl).toFixed(0)}
          </span>
        )}
        {canSplit && (
          <button
            className="live-order-split-btn"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setShowSplitModal(true);
            }}
            title="Split order into two"
          >
            {/* A lucide icon, not a literal glyph. This was U+2AFF (N-ARY WHITE
                VERTICAL BAR) — an obscure maths character most fonts do not
                ship, so it rendered as tofu: an empty box where the icon should
                be. Every other control in this label was already an icon, which
                is why only this one looked broken. */}
            <Split size={11} />
          </button>
        )}
        <button
          className="live-order-cancel-btn"
          onClick={handleCancelOrder}
          title="Cancel order"
        >
          ×
        </button>
      </div>

      {/* Split Order Modal: on document.body, so the price-pane clip (and any
          transformed chart ancestor) can never cut it. */}
      {showSplitModal && onSplitOrder && createPortal(
        <SplitOrderModal
          order={order}
          isLongPosition={isLongPosition}
          onSplit={onSplitOrder}
          onClose={() => setShowSplitModal(false)}
        />,
        document.body,
      )}
    </>
  );
});

export default LiveOrderLine;
